import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { FaPlay, FaPause } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, FrontAppItem, TimeTrackerCellProps } from '../../../../@types';
import {
    startFrontAppTimer,
    pauseFrontAppTimer,
    resumeFrontAppTimer
} from '../../../store/actions/frontApp.Action';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from 'theme-ui';
import { useLocation, useNavigate } from 'react-router-dom';

export const formatDisplayTime = (ms: number, isRunning?: boolean): string => {
    if (ms <= 0) return isRunning ? '0m 00s' : '0m';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (isRunning) {
        if (hours > 0) {
            return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
        }
        return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    }

    if (hours > 0) {
        return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    }
    return `${minutes}m`;
};
export const TimeTrackerCell: React.FC<TimeTrackerCellProps> = ({ item, colorMode, isCompletedTab }) => {
    const [now, setNow] = useState<number>(Date.now());
    const [loading, setLoading] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const auther = useSelector((state: RootState) => state.auth?.auther);
    const activeFrontAppTimer = useSelector((state: RootState) => state.frontApp?.runningFrontAppTask);
    const frontAppData = useSelector((state: RootState) => state.frontApp?.frontAppData?.data);
    const frontAppCompletedData = useSelector((state: RootState) => state.frontApp?.frontappCompletedData?.data);
    const allTasks = [...(frontAppData || []), ...(frontAppCompletedData || [])] as FrontAppItem[];
    const isAnyTimerRunning = allTasks.some((task) => task?.timer?.isRunning && task?.manager?._id === auther?._id);
    // Allow any role EXCEPT 'task worker' to act as the manager and start timers
    const isTaskManager = auther?.Role?.some((role) => role.RoleName?.toLowerCase() !== "task worker");
    const isManagerMatch = item?.manager?._id === auther?._id;
    const userDepartments = [
        ...((auther as { departments?: unknown[] })?.departments || [])
    ];
    const isDepartmentMatch = userDepartments.some((val: unknown) => {
        const name = typeof val === 'string' ? val : (val as { name?: string })?.name;
        if (!name || !item?.tabname) return false;
        const tabNameClean = item.tabname.replace(/#/g, '').toLowerCase().trim();
        const depNameClean = name.toLowerCase().trim();
        return tabNameClean === depNameClean || tabNameClean.includes(depNameClean);
    });
    const isAuthorized = isManagerMatch || isDepartmentMatch;
    const isDTCompleted = item?.DT === "Completed";
    const timer = item?.timer;
    const isRunning = timer?.isRunning || false;
    const hasTime = (timer?.accumulatedTime || 0) > 0 || isRunning;
    const requestRef = useRef<number>();
    const lastUpdateRef = useRef<number>(Math.floor(Date.now() / 1000));
    const animateTimer = useCallback(() => {
        const currentNow = Date.now();
        const currentSec = Math.floor(currentNow / 1000);
        if (currentSec !== lastUpdateRef.current) {
            setNow(currentNow);
            lastUpdateRef.current = currentSec;
        }
        requestRef.current = requestAnimationFrame(animateTimer);
    }, []);

    useEffect(() => {
        if (!isRunning) return;
        setNow(Date.now());
        lastUpdateRef.current = Math.floor(Date.now() / 1000);
        requestRef.current = requestAnimationFrame(animateTimer);
        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [isRunning, animateTimer]);

    let elapsedMs = timer?.accumulatedTime || 0;
    if (isRunning && timer?.lastStartedAt) {
        elapsedMs += now - new Date(timer.lastStartedAt).getTime();
    }
    const handleTimerClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDTCompleted) return;

        if (!isRunning) {
            const hasProject = !!(item as { projectId?: string })?.projectId;
            const hasManager = !!item?.manager?._id;

            if (!hasProject || !hasManager) {
                toast.error("Project and Task Manager selection is mandatory to start the timer.");
                return;
            }

            if (!isTaskManager || !isAuthorized) {
                toast.error("Access denied: This task is assigned to another manager and belongs to a different department.");
                return;
            }
            if (activeFrontAppTimer?.timer?.startedBy === auther?._id) {
                toast.error("You already have a timer running. Please pause it first.");
                return;
            }
            if (isAnyTimerRunning) {
                toast.error("You already have a timer running. Please pause it first.");
                return;
            }
        }

        if (!isRunning) {
            setShowConfirmModal(true);
        } else {
            setLoading(true);
            try {
                let skipRefetch = false;
                const searchParams = new URLSearchParams(location.search);
                if (searchParams.has('isRunning')) {
                    searchParams.delete('isRunning');
                    navigate(`${location.pathname}?${searchParams.toString()}`);
                    skipRefetch = true;
                }
                dispatch(pauseFrontAppTimer(item._id, null, skipRefetch));
            } catch (err: unknown) {
                const error = err as { response?: { data?: { message?: string } }; message?: string };
                console.error(error);
                const errMsg = error?.response?.data?.message || error?.message || "An error occurred";
                toast.error(errMsg);
            }
        }
    };

    const confirmStartTimer = async () => {
        setShowConfirmModal(false);
        setLoading(true);
        try {
            if (timer && timer.accumulatedTime > 0) {
                dispatch(resumeFrontAppTimer(item._id));
            } else {
                dispatch(startFrontAppTimer(item._id));
            }
        } catch (err: unknown) {
            const error = err as { response?: { data?: { message?: string } }; message?: string };
            console.error(error);
            const errMsg = error?.response?.data?.message || error?.message || "An error occurred";
            toast.error(errMsg);
        }
    };
    const isDark = colorMode === 'dark';
    const borderColor = isDark ? '#4a5a70' : '#d9d7dd';
    const textColor = isDark ? '#f7efe5' : '#4a4a4a';
    const hoverBg = isDark ? '#374358' : '#eef2f7';

    const isDisabled = isDTCompleted || (!isRunning && (isAnyTimerRunning));
    const effectiveHover = isHovered && !isDisabled;

    const buttonBorder = isRunning
        ? (effectiveHover ? '#ff4d4f' : (isDark ? '#56a0d3' : '#216ba5'))
        : (effectiveHover ? (isDark ? '#56a0d3' : '#216ba5') : borderColor);
    const buttonBg = isRunning
        ? (effectiveHover ? (isDark ? 'rgba(255,77,79,0.1)' : '#fff1f0') : (isDark ? 'rgba(86,160,211,0.1)' : '#e6f7ff'))
        : (effectiveHover ? hoverBg : 'transparent');
    const buttonColor = isRunning
        ? (effectiveHover ? '#ff4d4f' : (isDark ? '#56a0d3' : '#216ba5'))
        : (effectiveHover ? (isDark ? '#56a0d3' : '#216ba5') : textColor);

    if (isCompletedTab) {
        if (hasTime) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: textColor }}>
                        {formatDisplayTime(elapsedMs, false)}
                    </span>
                </div>
            );
        }
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '13px', color: textColor }}>-</span>
            </div>
        );
    }
    let tooltipTitle = isRunning ? "Pause Timer" : (hasTime ? "Resume Timer" : "Start Timer");
    if (isDTCompleted) {
        tooltipTitle = "Task is completed";
    } else if (!isRunning) {
        const hasProject = !!(item as { projectId?: string })?.projectId;
        const hasManager = !!item?.manager?._id;
        if (!hasProject || !hasManager) {
            tooltipTitle = "Project and Task Manager must be selected to start";
        } else if (!isTaskManager || !isAuthorized) {
            tooltipTitle = "Must be the assigned manager or in the task's department";
        } else if (isAnyTimerRunning) {
            tooltipTitle = "Another timer is already running";
        }
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: isDTCompleted ? 0.5 : 1 }}>
            {!hasTime ? (
                <button
                    onClick={handleTimerClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: `1px solid ${buttonBorder}`,
                        backgroundColor: buttonBg,
                        color: buttonColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        outline: 'none',
                        padding: 0,
                        boxShadow: isRunning && !isHovered ? `0 0 8px ${isDark ? 'rgba(86,160,211,0.3)' : 'rgba(33,107,165,0.2)'}` : 'none',
                        animation: isRunning && !isHovered ? 'frontAppTimerPulse 2s infinite alternate' : 'none'
                    }}
                    title={tooltipTitle}
                >
                    <FaPlay style={{ fontSize: '9px', marginLeft: '2px' }} />
                </button>
            ) : (
                <span
                    onClick={handleTimerClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    title={tooltipTitle}
                    style={{
                        fontSize: '13px',
                        fontWeight: isRunning ? 600 : 500,
                        color: isRunning ? (isDark ? '#56a0d3' : '#216ba5') : (effectiveHover ? (isDark ? '#56a0d3' : '#216ba5') : textColor),
                        userSelect: 'none',
                        border: `1px solid ${buttonBorder}`,
                        borderRadius: '16px',
                        padding: '0 12px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        minWidth: '50px',
                        backgroundColor: buttonBg,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isRunning && !isHovered ? `0 0 8px ${isDark ? 'rgba(86,160,211,0.3)' : 'rgba(33,107,165,0.2)'}` : 'none',
                        animation: isRunning && !isHovered ? 'frontAppTimerPulse 2s infinite alternate' : 'none'
                    }}
                >
                    {isRunning && <FaPause style={{ fontSize: '10px' }} />}
                    {!isRunning && effectiveHover && <FaPlay style={{ fontSize: '10px' }} />}
                    <span>{formatDisplayTime(elapsedMs, isRunning)}</span>
                </span>
            )}

            <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} size="sm" centered style={{ zIndex: 9999 }}>
                <ModalHeader toggle={() => setShowConfirmModal(false)} style={{ backgroundColor: isDark ? '#323c56' : '#ffffff', color: isDark ? '#ffffff' : '#000000' }}>Confirm Action</ModalHeader>
                <ModalBody style={{ backgroundColor: isDark ? '#323c56' : '#ffffff', color: isDark ? '#ffffff' : '#000000' }}>
                    Are you sure you want to {timer && timer.accumulatedTime > 0 ? 'resume' : 'start'} the timer for this task?
                </ModalBody>
                <ModalFooter style={{ backgroundColor: isDark ? '#323c56' : '#ffffff', display: 'flex', justifyContent: 'end', flexWrap: 'nowrap' }}>
                    <Button type="button" variant="CancelButton" sx={{ px: '24px', mr: 2 }} onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                    <Button type="button" variant="SaveButton" sx={{ px: '24px', cursor: 'pointer' }} onClick={confirmStartTimer}>Yes</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
